import { useQueries, useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';

export interface EmployeeLookup {
  id: string;
  first_name: string;
  last_name: string;
  email_primary: string;
  display_name: string;
}

interface RawEmployee {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email_primary?: string | null;
  display_name?: string | null;
}

interface ListResponse {
  success: boolean;
  data: EmployeeLookup[];
}

export interface UseEmployeesParams {
  search?: string;
  limit?: number;
  status?: 'active';
  ids?: string[];
}

function normalize(e: RawEmployee): EmployeeLookup {
  return {
    id: e.id,
    first_name: e.first_name ?? '',
    last_name: e.last_name ?? '',
    email_primary: e.email_primary ?? '',
    display_name: e.display_name
      ?? `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim()
      ?? '',
  };
}

/**
 * Hook gegen das BFF (`/api/v1/employees`). Das BFF proxiet zur Employee-API
 * und projiziert auf Lookup-Felder. Nutzt den BFF-Login-Token aus AuthContext.
 */
export function useEmployees(params?: UseEmployeesParams) {
  return useQuery<ListResponse>({
    queryKey: ['lookups', 'employees', params],
    queryFn: async () => {
      const query: Record<string, unknown> = {};
      if (params?.search) query.search = params.search;
      if (params?.limit !== undefined) query.limit = params.limit;
      if (params?.status) query.status = params.status;
      if (params?.ids?.length) query.ids = params.ids.join(',');
      try {
        const resp = await apiClient.get<RawEmployee[]>('/api/v1/employees', query);
        return {
          success: resp.success,
          data: (resp.data ?? []).map(normalize),
        };
      } catch {
        return { success: false, data: [] };
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Bulk-Resolve: ID-Liste → Map<id, EmployeeLookup>. Pro ID ein paralleler
 * Single-Fetch gegen `/api/v1/employees/:id`. React-Query cached pro ID.
 */
export function useEmployeesByIds(ids: string[]): { map: Map<string, EmployeeLookup>; isLoading: boolean } {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  const queries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['lookups', 'employee', id] as const,
      queryFn: async (): Promise<EmployeeLookup | null> => {
        try {
          const resp = await apiClient.get<RawEmployee>(`/api/v1/employees/${id}`);
          return resp.data ? normalize(resp.data) : null;
        } catch {
          return null;
        }
      },
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });
  const map = new Map<string, EmployeeLookup>();
  queries.forEach((q, idx) => {
    const id = uniqueIds[idx];
    if (id && q.data) map.set(id, q.data);
  });
  return { map, isLoading: queries.some((q) => q.isLoading) };
}
